/*
|--------------------------------------------------------------------------
| Decorator Utilities
|--------------------------------------------------------------------------
|
| Utility functions for Resourceful decorators. These functions help to
| ensure that information sent TO the database and information retrieved
| FROM the database are the expected shapes and types.
|
*/
import { DateTime } from 'luxon'
import { isString } from '@/private/utils'
import { isLuxonDateTime, isLucidBinaryValue, isObject, isArray } from './type_guards'
import {
  E_INVALID_PREPARED_VALUE,
  E_INVALID_CONSUMED_VALUE,
  E_UNCASTABLE,
} from '@nhtio/lucid-resourceful/errors'
import type { LucidBinaryValue, LucidPlainObject } from '@nhtio/lucid-resourceful/types'

/**
 * Casts an unknown value to a string
 * @param value - The value to cast
 * @returns The value as a string
 * @throws E_UNCASTABLE when the value cannot be cast to a string
 */
export const castValueAsString = (value: unknown): string => {
  if ('string' === typeof value) return value
  if ('number' === typeof value) return value.toString()
  throw new E_UNCASTABLE(['String'])
}
/**
 * Casts an unknown value to a Luxon DateTime with enhanced string parsing
 * @param value - The value to cast (string, number, Date, or DateTime)
 * @returns The value as a Luxon DateTime
 * @throws E_UNCASTABLE when the value cannot be cast to a DateTime
 * @remarks For string values, attempts SQL format parsing first, then falls back to ISO format parsing
 */
export const castValueAsDate = (value: unknown): DateTime => {
  switch (typeof value) {
    case 'string': {
      const ret = DateTime.fromSQL(value)
      if (!ret.isValid) {
        const fromIso = DateTime.fromISO(value)
        if (fromIso.isValid) return fromIso
        throw new E_UNCASTABLE(['Date'])
      }
      return ret
    }
    case 'number': {
      let ret = DateTime.fromSeconds(value)
      if (!ret.isValid) {
        throw new E_UNCASTABLE(['Date'])
      }
      if (ret.year > 9999 || ret.year < 0) {
        ret = DateTime.fromMillis(value)
      }
      return ret
    }
    case 'object': {
      if (isLuxonDateTime(value)) {
        return value
      }
      if (value instanceof Date) {
        const ret = DateTime.fromJSDate(value)
        if (!ret.isValid) {
          throw new E_UNCASTABLE(['Date'])
        }
        return ret
      }
      throw new E_UNCASTABLE(['Date'])
    }
    default: {
      throw new E_UNCASTABLE(['Date'])
    }
  }
}
/**
 * Casts an unknown value to a Luxon DateTime with enhanced error handling and string parsing
 * @param value - The value to cast (string, number, Date, or DateTime)
 * @returns The value as a Luxon DateTime
 * @throws E_UNCASTABLE when the value cannot be cast to a DateTime
 * @remarks For string values, attempts SQL format parsing first, then falls back to ISO format parsing
 */
export const castValueAsDateTime = (value: unknown): DateTime => {
  try {
    return castValueAsDate(value)
  } catch {
    throw new E_UNCASTABLE(['DateTime'])
  }
}
/**
 * Casts an unknown value to a Uint8Array (binary data)
 * @param value - The value to cast
 * @returns The value as a Uint8Array
 * @throws E_UNCASTABLE when the value cannot be cast to binary data
 */
export const castValueAsBinary = (value: unknown): Uint8Array => {
  if (isLucidBinaryValue(value)) return value
  throw new E_UNCASTABLE(['Binary'])
}
/**
 * Casts an unknown value to a number
 * @param value - The value to cast
 * @returns The value as a number
 * @throws E_UNCASTABLE when the value cannot be cast to a number
 */
export const castValueAsNumber = (value: unknown): number => {
  if ('number' === typeof value) return value
  if ('string' === typeof value) {
    const parsed = Number(value)
    if (Number.isNaN(parsed)) {
      throw new E_UNCASTABLE(['Number'])
    }
    return parsed
  }
  throw new E_UNCASTABLE(['Number'])
}
/**
 * Casts an unknown value to an integer
 * @param value - The value to cast
 * @returns The value as an integer (truncated if necessary)
 * @throws E_UNCASTABLE when the value cannot be cast to an integer
 */
export const castValueAsInteger = (value: unknown): number => {
  const num = castValueAsNumber(value)
  return Math.trunc(num)
}
/**
 * Casts an unknown value to a bigint
 * @param value - The value to cast
 * @returns The value as a bigint
 * @throws E_UNCASTABLE when the value cannot be cast to a bigint
 */
export const castValueAsBigint = (value: unknown): bigint => {
  try {
    if (typeof value === 'bigint') return value
    const num = castValueAsNumber(value)
    return BigInt(num)
  } catch {
    throw new E_UNCASTABLE(['Bigint'])
  }
}
/**
 * Casts an unknown value to an unsigned integer
 * @param value - The value to cast
 * @returns The value as an unsigned integer (32-bit)
 * @throws E_UNCASTABLE when the value cannot be cast to an unsigned integer
 */
export const castValueAsUnsignedInt = (value: unknown): number => {
  try {
    const num = typeof value === 'bigint' ? Number(value) : castValueAsNumber(value)
    if (!Number.isFinite(num) || Number.isNaN(num)) {
      throw new E_UNCASTABLE(['Unsignedint'])
    }
    return num >>> 0
  } catch {
    throw new E_UNCASTABLE(['Unsignedint'])
  }
}
/**
 * Casts an unknown value to a boolean
 * @param value - The value to cast
 * @returns The value as a boolean
 * @throws E_UNCASTABLE when the value cannot be cast to a boolean
 */
export const castValueAsBoolean = (value: unknown): boolean => {
  if ('boolean' === typeof value) return value
  if ('string' === typeof value) {
    const lower = value.toLowerCase()
    if (['true', '1'].includes(lower)) return true
    if (['false', '0'].includes(lower)) return false
    throw new E_UNCASTABLE(['Boolean'])
  }
  if ('number' === typeof value) {
    if (value === 0) return false
    if (value === 1) return true
    throw new E_UNCASTABLE(['Boolean'])
  }
  throw new E_UNCASTABLE(['Boolean'])
}
/**
 * Casts an unknown value to a plain object
 * @param value - The value to cast
 * @returns The value as a LucidPlainObject
 * @throws E_UNCASTABLE when the value cannot be cast to an object
 */
export const castValueAsObject = (value: unknown): LucidPlainObject => {
  if (isObject(value)) return value
  throw new E_UNCASTABLE(['Object'])
}
/**
 * Casts an unknown value to an array
 * @param value - The value to cast
 * @returns The value as an array of unknown elements
 * @throws E_UNCASTABLE when the value cannot be cast to an array
 */
export const castValueAsArray = (value: unknown): Array<unknown> => {
  if (isArray(value)) return value
  throw new E_UNCASTABLE(['Array'])
}

/**
 * Prepares a string value for database storage with nullable support
 * @param key - The field name for error reporting
 * @param value - The value to prepare
 * @param nullable - Whether the field can be null
 * @returns The prepared string value or null if nullable and value is null
 * @throws E_INVALID_PREPARED_VALUE when the value cannot be prepared
 */
export function prepareString(key: string, value: unknown, nullable: true): string | null
export function prepareString(key: string, value: unknown, nullable?: false): string
export function prepareString(
  key: string,
  value: unknown,
  nullable: boolean = false
): string | null {
  if (value === null && nullable === true) return null
  try {
    return castValueAsString(value)
  } catch {
    if (true === nullable) {
      return null
    }
    throw new E_INVALID_PREPARED_VALUE([key, 'String'])
  }
}
/**
 * Prepares a date value for database storage with nullable support and enhanced string parsing
 * @param key - The field name for error reporting
 * @param value - The value to prepare
 * @param nullable - Whether the field can be null
 * @returns The prepared DateTime value or null if nullable and value is null
 * @throws E_INVALID_PREPARED_VALUE when the value cannot be prepared
 * @remarks For string values, attempts SQL format parsing first, then falls back to ISO format parsing
 */
export function prepareDate(key: string, value: unknown, nullable: true): Date | null
export function prepareDate(key: string, value: unknown, nullable?: false): Date
export function prepareDate(key: string, value: unknown, nullable: boolean = false): Date | null {
  if (value === null && nullable === true) return null
  try {
    const dto = castValueAsDate(value)
    return dto.toJSDate()
  } catch {
    if (true === nullable) {
      return null
    }
    throw new E_INVALID_PREPARED_VALUE([key, 'Date'])
  }
}
/**
 * Prepares a datetime value for database storage with nullable support and enhanced string parsing
 * @param key - The field name for error reporting
 * @param value - The value to prepare
 * @param nullable - Whether the field can be null
 * @returns The prepared DateTime value or null if nullable and value is null
 * @throws E_INVALID_PREPARED_VALUE when the value cannot be prepared
 * @remarks For string values, attempts SQL format parsing first, then falls back to ISO format parsing
 */
export function prepareDateTime(key: string, value: unknown, nullable: true): Date | null
export function prepareDateTime(key: string, value: unknown, nullable?: false): Date
export function prepareDateTime(
  key: string,
  value: unknown,
  nullable: boolean = false
): Date | null {
  if (value === null && nullable === true) return null
  try {
    const dto = castValueAsDateTime(value)
    return dto.toJSDate()
  } catch {
    if (true === nullable) {
      return null
    }
    throw new E_INVALID_PREPARED_VALUE([key, 'DateTime'])
  }
}
/**
 * Prepares a binary value for database storage with nullable support
 * @param key - The field name for error reporting
 * @param value - The value to prepare
 * @param nullable - Whether the field can be null
 * @returns The prepared binary value or null if nullable and value is null
 * @throws E_INVALID_PREPARED_VALUE when the value cannot be prepared
 */
export function prepareBinary(key: string, value: unknown, nullable: true): LucidBinaryValue | null
export function prepareBinary(key: string, value: unknown, nullable?: false): LucidBinaryValue
export function prepareBinary(
  key: string,
  value: unknown,
  nullable: boolean = false
): LucidBinaryValue | null {
  if (value === null && nullable === true) return null
  try {
    return castValueAsBinary(value)
  } catch {
    if (true === nullable) {
      return null
    }
    throw new E_INVALID_PREPARED_VALUE([key, 'Binary'])
  }
}
/**
 * Prepares a number value for database storage with nullable support
 * @param key - The field name for error reporting
 * @param value - The value to prepare
 * @param nullable - Whether the field can be null
 * @returns The prepared number value or null if nullable and value is null
 * @throws E_INVALID_PREPARED_VALUE when the value cannot be prepared
 */
export function prepareNumber(key: string, value: unknown, nullable: true): number | null
export function prepareNumber(key: string, value: unknown, nullable?: false): number
export function prepareNumber(
  key: string,
  value: unknown,
  nullable: boolean = false
): number | null {
  if (value === null && nullable === true) return null
  try {
    return castValueAsNumber(value)
  } catch {
    if (true === nullable) {
      return null
    }
    throw new E_INVALID_PREPARED_VALUE([key, 'Number'])
  }
}
/**
 * Prepares an integer value for database storage with nullable support
 * @param key - The field name for error reporting
 * @param value - The value to prepare
 * @param nullable - Whether the field can be null
 * @returns The prepared integer value or null if nullable and value is null
 * @throws E_INVALID_PREPARED_VALUE when the value cannot be prepared
 */
export function prepareInteger(key: string, value: unknown, nullable: true): number | null
export function prepareInteger(key: string, value: unknown, nullable?: false): number
export function prepareInteger(
  key: string,
  value: unknown,
  nullable: boolean = false
): number | null {
  if (value === null && nullable === true) return null
  try {
    return castValueAsInteger(value)
  } catch {
    if (true === nullable) {
      return null
    }
    throw new E_INVALID_PREPARED_VALUE([key, 'Integer'])
  }
}
/**
 * Prepares a bigint value for database storage with nullable support
 * @param key - The field name for error reporting
 * @param value - The value to prepare
 * @param nullable - Whether the field can be null
 * @returns The prepared bigint value or null if nullable and value is null
 * @throws E_INVALID_PREPARED_VALUE when the value cannot be prepared
 */
export function prepareBigint(key: string, value: unknown, nullable: true): bigint | null
export function prepareBigint(key: string, value: unknown, nullable?: false): bigint
export function prepareBigint(
  key: string,
  value: unknown,
  nullable: boolean = false
): bigint | null {
  if (value === null && nullable === true) return null
  try {
    return castValueAsBigint(value)
  } catch {
    if (true === nullable) {
      return null
    }
    throw new E_INVALID_PREPARED_VALUE([key, 'Bigint'])
  }
}
/**
 * Prepares an unsigned integer value for database storage with nullable support
 * @param key - The field name for error reporting
 * @param value - The value to prepare
 * @param nullable - Whether the field can be null
 * @returns The prepared unsigned integer value or null if nullable and value is null
 * @throws E_INVALID_PREPARED_VALUE when the value cannot be prepared
 */
export function prepareUnsignedint(key: string, value: unknown, nullable: true): number | null
export function prepareUnsignedint(key: string, value: unknown, nullable?: false): number
export function prepareUnsignedint(
  key: string,
  value: unknown,
  nullable: boolean = false
): number | null {
  if (value === null && nullable === true) return null
  try {
    return castValueAsUnsignedInt(value)
  } catch {
    if (true === nullable) {
      return null
    }
    throw new E_INVALID_PREPARED_VALUE([key, 'Unsignedint'])
  }
}
/**
 * Prepares a boolean value for database storage with nullable support
 * @param key - The field name for error reporting
 * @param value - The value to prepare
 * @param nullable - Whether the field can be null
 * @returns The prepared boolean value or null if nullable and value is null
 * @throws E_INVALID_PREPARED_VALUE when the value cannot be prepared
 */
export function prepareBoolean(key: string, value: unknown, nullable: true): boolean | null
export function prepareBoolean(key: string, value: unknown, nullable?: false): boolean
export function prepareBoolean(
  key: string,
  value: unknown,
  nullable: boolean = false
): boolean | null {
  if (value === null && nullable === true) return null
  try {
    return castValueAsBoolean(value)
  } catch {
    if (true === nullable) {
      return null
    }
    throw new E_INVALID_PREPARED_VALUE([key, 'Boolean'])
  }
}
/**
 * Prepares an object value for database storage with nullable support
 * @param key - The field name for error reporting
 * @param value - The value to prepare
 * @param nullable - Whether the field can be null
 * @returns The prepared object value or null if nullable and value is null
 * @throws E_INVALID_PREPARED_VALUE when the value cannot be prepared
 */
export function prepareObject(key: string, value: unknown, nullable: true): LucidPlainObject | null
export function prepareObject(key: string, value: unknown, nullable?: false): LucidPlainObject
export function prepareObject(
  key: string,
  value: unknown,
  nullable: boolean = false
): LucidPlainObject | null {
  if (value === null && nullable === true) return null
  try {
    return castValueAsObject(value)
  } catch {
    if (true === nullable) {
      return null
    }
    throw new E_INVALID_PREPARED_VALUE([key, 'Object'])
  }
}
/**
 * Prepares an array value for database storage with nullable support
 * @param key - The field name for error reporting
 * @param value - The value to prepare
 * @param nullable - Whether the field can be null
 * @returns The prepared array value or null if nullable and value is null
 * @throws E_INVALID_PREPARED_VALUE when the value cannot be prepared
 */
export function prepareArray(key: string, value: unknown, nullable: true): Array<unknown> | null
export function prepareArray(key: string, value: unknown, nullable?: false): Array<unknown>
export function prepareArray(
  key: string,
  value: unknown,
  nullable: boolean = false
): Array<unknown> | null {
  if (value === null && nullable === true) return null
  try {
    return castValueAsArray(value)
  } catch {
    if (true === nullable) {
      return null
    }
    throw new E_INVALID_PREPARED_VALUE([key, 'Array'])
  }
}

/**
 * Consumes a string value from database results with nullable support
 * @param key - The field name for error reporting
 * @param value - The value to consume
 * @param nullable - Whether the field can be null
 * @returns The consumed string value or null if nullable and value is null
 * @throws E_INVALID_CONSUMED_VALUE when the value cannot be consumed
 */
export function consumeString(key: string, value: unknown, nullable: true): string | null
export function consumeString(key: string, value: unknown, nullable?: false): string
export function consumeString(
  key: string,
  value: unknown,
  nullable: boolean = false
): string | null {
  if (value === null && nullable === true) return null
  try {
    return castValueAsString(value)
  } catch {
    if (true === nullable) {
      return null
    }
    throw new E_INVALID_CONSUMED_VALUE([key, value, 'String'])
  }
}
/**
 * Consumes a date value from database results with nullable support and enhanced string parsing
 * @param key - The field name for error reporting
 * @param value - The value to consume
 * @param nullable - Whether the field can be null
 * @returns The consumed DateTime value or null if nullable and value is null
 * @throws E_INVALID_CONSUMED_VALUE when the value cannot be consumed
 * @remarks For string values, attempts SQL format parsing first, then falls back to ISO format parsing
 */
export function consumeDate(key: string, value: unknown, nullable: true): DateTime | null
export function consumeDate(key: string, value: unknown, nullable?: false): DateTime
export function consumeDate(
  key: string,
  value: unknown,
  nullable: boolean = false
): DateTime | null {
  if (value === null && nullable === true) return null
  try {
    return castValueAsDate(value)
  } catch {
    if (true === nullable) {
      return null
    }
    throw new E_INVALID_CONSUMED_VALUE([key, value, 'Date'])
  }
}
/**
 * Consumes a datetime value from database results with nullable support and enhanced string parsing
 * @param key - The field name for error reporting
 * @param value - The value to consume
 * @param nullable - Whether the field can be null
 * @returns The consumed DateTime value or null if nullable and value is null
 * @throws E_INVALID_CONSUMED_VALUE when the value cannot be consumed
 * @remarks For string values, attempts SQL format parsing first, then falls back to ISO format parsing
 */
export function consumeDateTime(key: string, value: unknown, nullable: true): DateTime | null
export function consumeDateTime(key: string, value: unknown, nullable?: false): DateTime
export function consumeDateTime(
  key: string,
  value: unknown,
  nullable: boolean = false
): DateTime | null {
  if (value === null && nullable === true) return null
  try {
    return castValueAsDateTime(value)
  } catch {
    if (true === nullable) {
      return null
    }
    throw new E_INVALID_CONSUMED_VALUE([key, value, 'DateTime'])
  }
}
/**
 * Consumes a binary value from database results with nullable support
 * @param key - The field name for error reporting
 * @param value - The value to consume
 * @param nullable - Whether the field can be null
 * @returns The consumed LucidBinaryValue or null if nullable and value is null
 * @throws E_INVALID_CONSUMED_VALUE when the value cannot be consumed
 */
export function consumeBinary(key: string, value: unknown, nullable: true): LucidBinaryValue | null
export function consumeBinary(key: string, value: unknown, nullable?: false): LucidBinaryValue
export function consumeBinary(
  key: string,
  value: unknown,
  nullable: boolean = false
): LucidBinaryValue | null {
  if (value === null && nullable === true) return null
  try {
    return castValueAsBinary(value)
  } catch {
    if (true === nullable) {
      return null
    }
    throw new E_INVALID_CONSUMED_VALUE([key, value, 'Binary'])
  }
}
/**
 * Consumes a number value from database results with nullable support
 * @param key - The field name for error reporting
 * @param value - The value to consume
 * @param nullable - Whether the field can be null
 * @returns The consumed number value or null if nullable and value is null
 * @throws E_INVALID_CONSUMED_VALUE when the value cannot be consumed
 */
export function consumeNumber(key: string, value: unknown, nullable: true): number | null
export function consumeNumber(key: string, value: unknown, nullable?: false): number
export function consumeNumber(
  key: string,
  value: unknown,
  nullable: boolean = false
): number | null {
  if (value === null && nullable === true) return null
  try {
    return castValueAsNumber(value)
  } catch {
    if (true === nullable) {
      return null
    }
    throw new E_INVALID_CONSUMED_VALUE([key, value, 'Number'])
  }
}
/**
 * Consumes an integer value from database results with nullable support
 * @param key - The field name for error reporting
 * @param value - The value to consume
 * @param nullable - Whether the field can be null
 * @returns The consumed integer value or null if nullable and value is null
 * @throws E_INVALID_CONSUMED_VALUE when the value cannot be consumed
 */
export function consumeInteger(key: string, value: unknown, nullable: true): number | null
export function consumeInteger(key: string, value: unknown, nullable?: false): number
export function consumeInteger(
  key: string,
  value: unknown,
  nullable: boolean = false
): number | null {
  if (value === null && nullable === true) return null
  try {
    return castValueAsInteger(value)
  } catch {
    if (true === nullable) {
      return null
    }
    throw new E_INVALID_CONSUMED_VALUE([key, value, 'Integer'])
  }
}
/**
 * Consumes a bigint value from database results with nullable support
 * @param key - The field name for error reporting
 * @param value - The value to consume
 * @param nullable - Whether the field can be null
 * @returns The consumed bigint value or null if nullable and value is null
 * @throws E_INVALID_CONSUMED_VALUE when the value cannot be consumed
 */
export function consumeBigint(key: string, value: unknown, nullable: true): bigint | null
export function consumeBigint(key: string, value: unknown, nullable?: false): bigint
export function consumeBigint(
  key: string,
  value: unknown,
  nullable: boolean = false
): bigint | null {
  if (value === null && nullable === true) return null
  try {
    return castValueAsBigint(value)
  } catch {
    if (true === nullable) {
      return null
    }
    throw new E_INVALID_CONSUMED_VALUE([key, value, 'Bigint'])
  }
}
/**
 * Consumes an unsigned integer value from database results with nullable support
 * @param key - The field name for error reporting
 * @param value - The value to consume
 * @param nullable - Whether the field can be null
 * @returns The consumed unsigned integer value or null if nullable and value is null
 * @throws E_INVALID_CONSUMED_VALUE when the value cannot be consumed
 */
export function consumeUnsignedint(key: string, value: unknown, nullable: true): number | null
export function consumeUnsignedint(key: string, value: unknown, nullable?: false): number
export function consumeUnsignedint(
  key: string,
  value: unknown,
  nullable: boolean = false
): number | null {
  if (value === null && nullable === true) return null
  try {
    return castValueAsUnsignedInt(value)
  } catch {
    if (true === nullable) {
      return null
    }
    throw new E_INVALID_CONSUMED_VALUE([key, value, 'Unsignedint'])
  }
}
/**
 * Consumes a boolean value from database results with nullable support
 * @param key - The field name for error reporting
 * @param value - The value to consume
 * @param nullable - Whether the field can be null
 * @returns The consumed boolean value or null if nullable and value is null
 * @throws E_INVALID_CONSUMED_VALUE when the value cannot be consumed
 */
export function consumeBoolean(key: string, value: unknown, nullable: true): boolean | null
export function consumeBoolean(key: string, value: unknown, nullable?: false): boolean
export function consumeBoolean(
  key: string,
  value: unknown,
  nullable: boolean = false
): boolean | null {
  if (value === null && nullable === true) return null
  try {
    return castValueAsBoolean(value)
  } catch {
    if (true === nullable) {
      return null
    }
    throw new E_INVALID_CONSUMED_VALUE([key, value, 'Boolean'])
  }
}
/**
 * Consumes an object value from database results with nullable support
 * @param key - The field name for error reporting
 * @param value - The value to consume
 * @param nullable - Whether the field can be null
 * @returns The consumed LucidPlainObject or null if nullable and value is null
 * @throws E_INVALID_CONSUMED_VALUE when the value cannot be consumed
 */
export function consumeObject(key: string, value: unknown, nullable: true): LucidPlainObject | null
export function consumeObject(key: string, value: unknown, nullable?: false): LucidPlainObject
export function consumeObject(
  key: string,
  value: unknown,
  nullable: boolean = false
): LucidPlainObject | null {
  if (value === null && nullable === true) return null
  try {
    if (isString(value)) {
      value = JSON.parse(value)
    }

    return castValueAsObject(value)
  } catch {
    if (true === nullable) {
      return null
    }
    throw new E_INVALID_CONSUMED_VALUE([key, value, 'Object'])
  }
}
/**
 * Consumes an array value from database results with nullable support
 * @param key - The field name for error reporting
 * @param value - The value to consume
 * @param nullable - Whether the field can be null
 * @returns The consumed array value or null if nullable and value is null
 * @throws E_INVALID_CONSUMED_VALUE when the value cannot be consumed
 */
export function consumeArray(key: string, value: unknown, nullable: true): Array<unknown> | null
export function consumeArray(key: string, value: unknown, nullable?: false): Array<unknown>
export function consumeArray(
  key: string,
  value: unknown,
  nullable: boolean = false
): Array<unknown> | null {
  if (value === null && nullable === true) return null
  try {
    return castValueAsArray(value)
  } catch {
    if (true === nullable) {
      return null
    }
    throw new E_INVALID_CONSUMED_VALUE([key, value, 'Array'])
  }
}
